import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Alert,
  IconButton,
  Grid,
  Chip,
  CircularProgress,
  Divider,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon, Info as InfoIcon } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { performLeaveAdjustment } from "store/features/company/leaveManagement/leaveAdjustmentReimbursement/leaveAdjustmentReimbursementActions";
import { selectAdjusting } from "store/features/company/leaveManagement/leaveAdjustmentReimbursement/leaveAdjustmentReimbursementSelectors";
import { themeSettings } from "theme/themeConfig";

const AdjustLeaveModal = ({ open, onClose, selectedBalance }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const adjusting = useSelector(selectAdjusting);

  const baseRadius = theme.shape?.borderRadius || themeSettings.borderRadius || 12;

  const [formData, setFormData] = useState({
    employee_id: "",
    leave_type_id: "",
    action: "add",
    days: "",
    reason: "",
  });

  const [errorMsg, setErrorMsg] = useState("");
  const [infoMsg, setInfoMsg] = useState("");

  useEffect(() => {
    if (selectedBalance) {
      setFormData({
        employee_id: selectedBalance.employee_id || "",
        leave_type_id: selectedBalance.leave_type_id || "",
        action: "add",
        days: "",
        reason: "",
      });
    } else {
      setFormData({
        employee_id: "",
        leave_type_id: "",
        action: "add",
        days: "",
        reason: "",
      });
    }
    setErrorMsg("");
    setInfoMsg("");
  }, [selectedBalance, open]);

  // Active leave balance details
  const activeBalance = selectedBalance || null;
  const maxPolicy = activeBalance ? parseFloat(activeBalance.max_days_per_year) || 0 : 0;
  const currentTotal = activeBalance ? parseFloat(activeBalance.total_days) || 0 : 0;
  const currentUsed = activeBalance ? parseFloat(activeBalance.used_days) || 0 : 0;
  const currentAvailable = activeBalance ? parseFloat(activeBalance.available_days) || 0 : 0;

  // Real-time policy validation
  useEffect(() => {
    setErrorMsg("");
    setInfoMsg("");

    const numDays = parseFloat(formData.days) || 0;
    if (numDays <= 0) return;

    if (formData.action === "add") {
      if (maxPolicy > 0 && currentAvailable + numDays > maxPolicy) {
        setErrorMsg(
          `Company Policy Limit Exceeded: Max annual limit for ${activeBalance?.leave_type_name || "this leave"} is ${maxPolicy} days. Adding ${numDays} days would make total available balance ${currentAvailable + numDays} days.`
        );
      } else {
        if (currentUsed > 0) {
          const restoredUsed = Math.min(currentUsed, numDays);
          const addedTotal = numDays - restoredUsed;
          if (addedTotal > 0) {
            setInfoMsg(
              `Adding ${numDays} days will restore ${restoredUsed} used days and increase total quota by ${addedTotal} days.`
            );
          } else {
            setInfoMsg(
              `Adding ${numDays} days will restore ${numDays} used days back to available balance (Available: ${currentAvailable} → ${currentAvailable + numDays} days).`
            );
          }
        } else {
          setInfoMsg(
            `Adding ${numDays} days will update total quota from ${currentTotal} to ${currentTotal + numDays} days.`
          );
        }
      }
    } else if (formData.action === "deduct") {
      if (numDays > currentAvailable) {
        setErrorMsg(`Insufficient Balance: Available leave balance is only ${currentAvailable} days.`);
      } else {
        setInfoMsg(
          `Deducting ${numDays} days will reduce available balance from ${currentAvailable} to ${currentAvailable - numDays} days.`
        );
      }
    }
  }, [formData.days, formData.action, currentTotal, currentUsed, currentAvailable, maxPolicy, activeBalance]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (errorMsg) return;

    if (!formData.employee_id || !formData.leave_type_id || !formData.action || !formData.days) {
      setErrorMsg("Please fill in all required fields.");
      return;
    }

    const result = await dispatch(performLeaveAdjustment(formData));
    if (result?.success) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: { borderRadius: `${baseRadius + 4}px` },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700 }}>
          Adjust Employee Leave Balance
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ p: 3 }}>
          {/* Employee Leave Balance Info */}
          {activeBalance && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: `${baseRadius}px`,
                bgcolor: "background.neutral",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
                {activeBalance.first_name} {activeBalance.last_name} ({activeBalance.employee_code || "EMP"})
              </Typography>
              <Grid container spacing={1} sx={{ fontSize: "0.85rem" }}>
                <Grid item xs={6}>
                  <strong>Leave Type:</strong> {activeBalance.leave_type_name}
                </Grid>
                <Grid item xs={6}>
                  <strong>Policy Limit:</strong> {maxPolicy > 0 ? `${maxPolicy} Days / Year` : "Unlimited"}
                </Grid>

                <Grid item xs={4}>
                  <strong>Total Quota:</strong> {currentTotal} Days
                </Grid>
                <Grid item xs={4}>
                  <strong>Used Days:</strong> {currentUsed} Days
                </Grid>
                <Grid item xs={4}>
                  <strong>Available:</strong>{" "}
                  <Chip
                    label={`${currentAvailable} Days`}
                    size="small"
                    color={currentAvailable > 0 ? "success" : "default"}
                    sx={{ fontWeight: 700, height: 20 }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Action"
                name="action"
                value={formData.action}
                onChange={handleChange}
                required
              >
                <MenuItem value="add">Add Leave (Increase Balance)</MenuItem>
                <MenuItem value="deduct">Deduct Leave (Decrease Balance)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Number of Days"
                name="days"
                value={formData.days}
                onChange={handleChange}
                inputProps={{ step: "0.5", min: "0.5" }}
                required
                placeholder="e.g. 1.0, 2.5"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason / Remarks (Mandatory)"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                required
                placeholder="State reason for adding or deducting leave..."
              />
            </Grid>
          </Grid>

          {/* Feedback */}
          {errorMsg && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: `${baseRadius}px` }}>
              {errorMsg}
            </Alert>
          )}
          {infoMsg && !errorMsg && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2, borderRadius: `${baseRadius}px` }}>
              {infoMsg}
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button onClick={onClose} disabled={adjusting} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={adjusting || !!errorMsg || !formData.days || !formData.reason}
            startIcon={adjusting ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ px: 3, fontWeight: 700 }}
          >
            {adjusting ? "Processing..." : formData.action === "add" ? "Add Leave" : "Deduct Leave"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AdjustLeaveModal;
